import { Download, Search } from "lucide-react";
import type { FuelActivityFilters } from "@/types/database";
import { Input } from "@/components/ui/Input";
import { FieldLabel } from "@/components/ui/Workspace";

const DATE_RANGES: { value: FuelActivityFilters["dateRange"]; label: string }[] = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "ytd", label: "YTD" },
  { value: "all", label: "All time" },
];

const SORT_OPTIONS: { value: FuelActivityFilters["sort"]; label: string }[] = [
  { value: "spent", label: "Total spend" },
  { value: "fillUps", label: "Fill-ups" },
  { value: "litres", label: "Litres" },
  { value: "recent", label: "Most recent" },
];

type Props = {
  filters: FuelActivityFilters;
  onChange: (next: FuelActivityFilters) => void;
  gradeOptions?: string[];
  carOptions?: { id: string; label: string }[];
  stationOptions?: { id: string; label: string }[];
  onExport?: () => void;
  exportLabel?: string;
  showSort?: boolean;
  showGrade?: boolean;
  showCar?: boolean;
  showStation?: boolean;
};

export function FuelActivityFiltersBar({
  filters,
  onChange,
  gradeOptions = [],
  carOptions = [],
  stationOptions = [],
  onExport,
  exportLabel = "Export CSV",
  showSort = true,
  showGrade = false,
  showCar = false,
  showStation = false,
}: Props) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="grid flex-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div>
          <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
          <Input
            placeholder="User, car, station…"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
          />
        </div>

        <div>
          <FieldLabel>Date range</FieldLabel>
          <select
            value={filters.dateRange}
            onChange={(e) =>
              onChange({ ...filters, dateRange: e.target.value as FuelActivityFilters["dateRange"] })
            }
            className="w-full rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {DATE_RANGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {showSort ? (
          <div>
            <FieldLabel>Sort by</FieldLabel>
            <select
              value={filters.sort}
              onChange={(e) =>
                onChange({ ...filters, sort: e.target.value as FuelActivityFilters["sort"] })
              }
              className="w-full rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showGrade && gradeOptions.length > 0 ? (
          <div>
            <FieldLabel>Fuel grade</FieldLabel>
            <select
              value={filters.fuelGrade}
              onChange={(e) => onChange({ ...filters, fuelGrade: e.target.value })}
              className="w-full rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">All grades</option>
              {gradeOptions.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showCar && carOptions.length > 0 ? (
          <div>
            <FieldLabel>Vehicle</FieldLabel>
            <select
              value={filters.carID}
              onChange={(e) => onChange({ ...filters, carID: e.target.value })}
              className="w-full rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">All vehicles</option>
              {carOptions.map((car) => (
                <option key={car.id} value={car.id}>
                  {car.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showStation && stationOptions.length > 0 ? (
          <div>
            <FieldLabel>Station</FieldLabel>
            <select
              value={filters.gasStationID}
              onChange={(e) => onChange({ ...filters, gasStationID: e.target.value })}
              className="w-full rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">All stations</option>
              {stationOptions.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {onExport ? (
        <button
          type="button"
          onClick={onExport}
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-canvas px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-canvas-hover"
        >
          <Download className="h-4 w-4" />
          {exportLabel}
        </button>
      ) : null}
    </div>
  );
}
